// ============================================================================
// Auto-reply — frontend module.
// Per-line (Marketing / Support) config editor + a live test-classifier box, backed by
// GET/POST /api/autoreply. Uses only existing shared classes (card/pad, toolbar, form-grid/
// field, h-input, btn/ghost, pill, msg) — no new stylesheet, inline style="" for small tweaks.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'

const LINES = [
  { id: 'marketing', label: 'Marketing' },
  { id: 'support', label: 'Support' },
]

export function renderAutoreply(el) {
  let line = 'marketing'

  el.innerHTML = `
    <div class="card pad">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Auto-reply</div>
          <div class="mod-sub">Keyword + AI-classified inbound responses, per line</div>
        </div>
        <div id="ar-lines" style="display:flex;gap:8px">
          ${LINES.map(l => `<button type="button" class="btn ghost" data-line="${l.id}">${esc(l.label)}</button>`).join('')}
        </div>
      </div>
      <div id="ar-body"><div class="msg">Loading configuration…</div></div>
    </div>`

  const lineBtns = el.querySelector('#ar-lines')
  const body = el.querySelector('#ar-body')

  function paintLineButtons() {
    lineBtns.querySelectorAll('button[data-line]').forEach(b => {
      b.classList.toggle('ghost', b.dataset.line !== line)
    })
  }

  lineBtns.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-line]')
    if (!btn || btn.dataset.line === line) return
    line = btn.dataset.line
    paintLineButtons()
    load()
  })

  async function load() {
    body.innerHTML = '<div class="msg">Loading configuration…</div>'
    try {
      const data = await api.get('/api/autoreply?line=' + encodeURIComponent(line))
      renderForm(body, line, data.config || {}, !!data.aiPresent)
    } catch (err) {
      body.innerHTML = '<div class="msg bad">Could not load configuration: ' + esc(err.message) + '</div>'
    }
  }

  paintLineButtons()
  load()
}

function renderForm(body, line, cfg, aiPresent) {
  body.innerHTML = `
    <div class="form-grid" style="margin-bottom:14px">
      <div class="field">
        <label>State</label>
        <label style="display:flex;align-items:center;gap:8px;font-weight:600;color:var(--text)">
          <input type="checkbox" id="ar-enabled" ${cfg.enabled ? 'checked' : ''}> Enabled
        </label>
      </div>
      <div class="field">
        <label>Classifier</label>
        <label style="display:flex;align-items:center;gap:8px;font-weight:600;color:var(--text)">
          <input type="checkbox" id="ar-useai" ${cfg.useAI ? 'checked' : ''}> Use AI intent classification
        </label>
      </div>
      <div class="field">
        <label for="ar-support-number">Support number</label>
        <input class="h-input" id="ar-support-number" type="text" value="${esc(cfg.supportNumber || '')}">
      </div>
      <div class="field">
        <label for="ar-throttle">Throttle (hours)</label>
        <input class="h-input" id="ar-throttle" type="number" min="1" max="168" value="${esc(cfg.throttleHours ?? 6)}">
      </div>
    </div>

    <div class="field" style="margin-bottom:14px">
      <label for="ar-reply">Reply text <span class="mod-sub" style="font-weight:600">— use {number} to insert the support number</span></label>
      <textarea class="h-input" id="ar-reply" rows="3">${esc(cfg.replyText || '')}</textarea>
    </div>

    <div class="field" style="margin-bottom:14px">
      <label for="ar-support-kw">Support keywords <span class="mod-sub" style="font-weight:600">— comma-separated; a match routes to a human</span></label>
      <input class="h-input" id="ar-support-kw" type="text" value="${esc(cfg.supportKeywords || '')}">
    </div>

    <div class="field" style="margin-bottom:14px">
      <label for="ar-dismiss-kw">Dismiss keywords <span class="mod-sub" style="font-weight:600">— comma-separated; a match is treated as a brush-off</span></label>
      <input class="h-input" id="ar-dismiss-kw" type="text" value="${esc(cfg.dismissKeywords || '')}">
    </div>

    <div class="toolbar" style="margin-bottom:2px">
      <button type="button" class="btn" id="ar-save">Save configuration</button>
      <div class="grow"></div>
    </div>
    <div class="msg" id="ar-save-msg"></div>

    <div class="table-wrap" style="margin-top:18px;padding:14px">
      <div class="mod-h">Test classifier</div>
      <div class="mod-sub" style="margin-bottom:10px">
        Classify a sample inbound with the ${esc(line)} config${aiPresent ? '' : ' (Workers AI not bound — keyword fallback only)'}.
      </div>
      <div class="field" style="margin-bottom:10px">
        <textarea class="h-input" id="ar-test-text" rows="2" placeholder="e.g. I never got my code, can someone call me?"></textarea>
      </div>
      <div class="toolbar" style="margin-bottom:2px">
        <button type="button" class="btn ghost" id="ar-test-btn">Test</button>
        <div class="grow"></div>
      </div>
      <div id="ar-test-out" class="msg"></div>
    </div>`

  const saveMsg = body.querySelector('#ar-save-msg')
  const saveBtn = body.querySelector('#ar-save')

  function collect() {
    return {
      enabled: body.querySelector('#ar-enabled').checked,
      useAI: body.querySelector('#ar-useai').checked,
      replyText: body.querySelector('#ar-reply').value,
      supportNumber: body.querySelector('#ar-support-number').value.trim(),
      throttleHours: Number(body.querySelector('#ar-throttle').value) || 6,
      supportKeywords: body.querySelector('#ar-support-kw').value,
      dismissKeywords: body.querySelector('#ar-dismiss-kw').value,
    }
  }

  saveBtn.addEventListener('click', async () => {
    saveMsg.className = 'msg'
    saveMsg.textContent = 'Saving…'
    saveBtn.disabled = true
    try {
      await api.post('/api/autoreply', { line, config: collect() })
      saveMsg.className = 'msg ok'
      saveMsg.textContent = 'Saved.'
    } catch (err) {
      saveMsg.className = 'msg bad'
      saveMsg.textContent = 'Save failed: ' + err.message
    } finally {
      saveBtn.disabled = false
    }
  })

  const testBtn = body.querySelector('#ar-test-btn')
  const testOut = body.querySelector('#ar-test-out')

  testBtn.addEventListener('click', async () => {
    const text = body.querySelector('#ar-test-text').value.trim()
    testOut.className = 'msg'
    if (!text) { testOut.className = 'msg bad'; testOut.textContent = 'Enter a message to test.'; return }
    testOut.textContent = 'Classifying…'
    testBtn.disabled = true
    try {
      const r = await api.post('/api/autoreply', { action: 'test', line, text })
      const via = r.decidedBy === 'ai' ? 'AI' : 'keywords'
      const pillClass = r.decidedBy === 'ai' ? 'pill an' : 'pill'
      testOut.className = 'msg'
      testOut.innerHTML =
        '<span class="pill mk" style="margin-right:6px">' + esc(String(r.label || 'other')) + '</span>' +
        '<span class="' + pillClass + '">decided by ' + esc(via) + '</span>' +
        (r.raw ? '<div class="mod-sub" style="margin-top:8px;font-weight:600">raw: ' + esc(String(r.raw)) + '</div>' : '')
    } catch (err) {
      testOut.className = 'msg bad'
      testOut.textContent = 'Test failed: ' + err.message
    } finally {
      testBtn.disabled = false
    }
  })
}
